import { API_ROOT_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';
import type {
  AdminProductionFactor,
  AdminProductionFactorsResponse,
  CreateAdminProductionFactorsBody,
  UpdateAdminProductionFactorBody,
} from './types';

/**
 * Wastage bands per facility process, served under
 * `/api/admin/fiber/production-factors` on `API_ROOT_URL`.
 *
 * Kept apart from `fetchFiberProductionFactors` in
 * `@/api/(admin)/fiber-production-processes`, which reads the single
 * `…/production-factors/:productCategoryId` band shown as a hint on the
 * production-process form.
 */

/** The list. Takes a pre-built query string; paging and the tab flag live in the hook. */
export async function fetchAdminProductionFactors(
  qs: string,
): Promise<AdminProductionFactorsResponse> {
  return apiFetch<AdminProductionFactorsResponse>(
    `/api/admin/fiber/production-factors?${qs}`,
    undefined,
    API_ROOT_URL,
  );
}

/**
 * Updates one band. Patches exactly the keys it is given; a key left out
 * keeps its stored value. Answers the `{ data, metadata }` envelope.
 */
export async function updateAdminProductionFactor(
  id: string,
  body: UpdateAdminProductionFactorBody,
): Promise<{ data: AdminProductionFactor }> {
  return apiFetch<{ data: AdminProductionFactor }>(
    `/api/admin/fiber/production-factors/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

/**
 * Creates one band per facility process in the list, all sharing the category
 * and the percentages — a POST to the collection. Answers 201 with the new
 * bands in the `{ data, metadata }` envelope.
 */
export async function createAdminProductionFactors(
  body: CreateAdminProductionFactorsBody,
): Promise<{ data: AdminProductionFactor[] }> {
  return apiFetch<{ data: AdminProductionFactor[] }>(
    '/api/admin/fiber/production-factors',
    { method: 'POST', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}
